import { useMemo, useState } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { EditBase, useGetMany, useGetOne, useRecordContext, useUpdate } from "react-admin";
import type { Student } from "../../../providers/studentsProvider";
import type { BookedQuote } from "../../../providers/bookedQuotesProvider";
import { StudentTheoryExamSimulationsField } from "../../../fields/StudentTheoryExamSimulationsField";
import EditIcon from "@mui/icons-material/EditOutlined";
import { overviewCardStyle } from "../../../misc/backofficeTheme";
import { InstructorIcon } from "../../../icons/InstructorIcon";
import { ExamIcon } from "../../../icons/ExamIcon";
import { calculateTheoryLearningProgress } from "../../../backoffice.utils";
import { ResetTheoryLearningButton } from "../../../buttons/ResetTheoryLearningButton";
import { grants } from "../../../backoffice.access_control";
import { Row } from "../../../misc/Row";
import { Column } from "../../../misc/Column";
import { DrivingSchool } from "../../../providers/drivingSchoolsProvider";
import { DateField } from "../../../fields/DateField";
import { TheoryExam } from "../../../model/autovioCalendarEvents";
import { DateTime } from "luxon";
import { TrainingItem } from "./TrainingItem";
import { Form2 } from "../../../misc/Form2";
import { BooleanInput } from "../../../inputs/BooleanInput";
import { type DeepPartial } from "ts-essentials";
import { useOnEscapeKeyDown } from "../../../hooks/useOnEscapeKeyDown";
import { asCategory, TheoryLessonCategory, TheoryLessonProductType } from "../../../model/ProductType";
import _maxBy from "lodash/maxBy";
import _minBy from "lodash/minBy";
import { formatDate } from "../../../utils/calendar";

export function TheoryProgressCard(props: { student: Student; selectedBookedQuote?: BookedQuote }) {
  const { student, selectedBookedQuote } = props;
  return (
    <Box sx={{ ...overviewCardStyle, padding: "24px 30px" }}>
      <_Theory student={student} selectedBookedQuote={selectedBookedQuote} />
    </Box>
  );
}

function _Theory(props: { student: Student; selectedBookedQuote?: BookedQuote }) {
  const { student, selectedBookedQuote } = props;
  const { data: drivingSchool } = useGetOne<DrivingSchool>("drivingSchools", { id: student.drivingSchoolId });
  const relevantBookedTrainings = (
    selectedBookedQuote
      ? student.bookedTrainings.filter((it) => selectedBookedQuote.bookedTrainingIds.includes(it.id))
      : student.bookedTrainings
  ).filter((it) => !it.isCancelled);
  const studentMustTakeATheoryExam = relevantBookedTrainings.some((it) => it.includesTheoryExam);
  const compulsoryTheoryLessons: Partial<Record<TheoryLessonCategory, number>> = {};
  for (const bookedTraining of relevantBookedTrainings) {
    for (const [productType, count] of Object.entries(bookedTraining.compulsoryTheoryLessons)) {
      const category = asCategory(productType as TheoryLessonProductType);
      if (category && count > (compulsoryTheoryLessons[category] ?? 0)) {
        compulsoryTheoryLessons[category] = count;
      }
    }
  }
  const studentCanLearnTheoryInMobileApp =
    (drivingSchool?.customizations ?? []).includes("theoryLearning") &&
    student.featureToggles.canLearnTheoryInMobileApp;
  const [isHasPassedTheoryExamEditable, setIsHasPassedTheoryExamEditable] = useState(false);
  const theoryExamIds = Object.keys(student.theoryKPIs?.theoryExams ?? {});
  const { data: theoryExams } = useGetMany<TheoryExam>(
    "calendarEvents",
    { ids: theoryExamIds },
    { enabled: theoryExamIds.length > 0 },
  );
  const [numTheoryExamsTaken, lastTheoryExamTakenAt, lastTheoryExamResult, lastTheoryExamResults, nextTheoryExam] =
    useMemo(() => {
      const takenTheoryExams = (theoryExams ?? []).filter(
        (it) => !it.deleted && it.student.rsvp === "accepted" && it.end < DateTime.now(),
      );
      const lastTakenTheoryExam = _maxBy(takenTheoryExams, (it) => it.start.toMillis());
      const scheduledTheoryExams = (theoryExams ?? []).filter(
        (it) => !it.deleted && it.student.rsvp === "accepted" && it.start > DateTime.now(),
      );
      const nextTheoryExam = _minBy(scheduledTheoryExams, (it) => it.start.toMillis());
      const perTrainingResults = Object.values(lastTakenTheoryExam?.theoryExamResults ?? {});
      const effectiveLastResult =
        perTrainingResults.find((it) => it === "failed") ??
        perTrainingResults[0] ??
        lastTakenTheoryExam?.theoryExamResult;
      return [
        takenTheoryExams.length,
        lastTakenTheoryExam?.start,
        effectiveLastResult,
        lastTakenTheoryExam?.theoryExamResults,
        nextTheoryExam,
      ];
    }, [theoryExams]);
  const drivingLicenseClassByTrainingId = useMemo(
    () => new Map(student.bookedTrainings.map((it) => [it.id, it.drivingLicenseClass])),
    [student.bookedTrainings],
  );
  const theoryLearningProgress = calculateTheoryLearningProgress(student.theoryKPIs?.theoryLearningProgress);
  const hasPassedTheoryExam = student.theoryKPIs?.hasPassedTheoryExam;
  const orderedCompulsoryTheoryLessons = (
    Object.entries(compulsoryTheoryLessons) as Array<[TheoryLessonCategory, number]>
  ).sort(([category1], [category2]) => {
    if (category1 === "Grundstoff") {
      return -1;
    }
    if (category2 === "Grundstoff") {
      return 1;
    }
    return 0;
  });

  return (
    <>
      <Typography sx={{ fontWeight: "bold", fontSize: 15, marginBottom: 1 }}>Theorie</Typography>
      <Box>
        <Grid
          container
          columnSpacing={2}
          rowSpacing={1}
          sx={{
            "@media (max-width: 359px)": {
              "& > .MuiGrid-item": { flexBasis: "100%", maxWidth: "100%" },
            },
          }}
        >
          {orderedCompulsoryTheoryLessons.map(([category, numCompulsoryLesson]) => (
            <Grid item xs={6} key={category}>
              <TrainingItem
                icon={<InstructorIcon />}
                label={category.replace("_", "\u00A0")}
                value={student.theoryKPIs?.finishedTheoryLessonsCountByCategory?.[category] ?? 0}
                outOf={numCompulsoryLesson}
                unit="Lekt."
              />
            </Grid>
          ))}
          {studentCanLearnTheoryInMobileApp && studentMustTakeATheoryExam && (
            <Grid item xs={6}>
              <TrainingItem
                icon={<ExamIcon />}
                value={theoryLearningProgress}
                label="Lernstand"
                unit="%"
                trailing={
                  theoryLearningProgress !== "0,0" && grants.has("resetTheoryLearning") && <ResetTheoryLearningButton />
                }
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {studentCanLearnTheoryInMobileApp && studentMustTakeATheoryExam && (
        <Box sx={{ marginTop: 2.5 }}>
          <StudentTheoryExamSimulationsField />
        </Box>
      )}
      <Box sx={{ marginTop: 2.5 }}>
        <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Letzte Theorielektion</Typography>
        <DateField source="theoryKPIs.mostRecentTheoryLessonDate" emptyText="-" />
      </Box>
      <Row gap="10px" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 2.5 }}>
        <Column sx={{ flex: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Theorieprüfung bestanden</Typography>
          {isHasPassedTheoryExamEditable ? (
            <_HasPassedTheoryExamForm stopEditMode={() => setIsHasPassedTheoryExamEditable(false)} />
          ) : (
            <Column gap={0.5}>
              <Row gap={1}>
                <Typography sx={{ fontSize: 14 }}>
                  {(() => {
                    if (!theoryExams) {
                      // `theoryExams` have not been loaded yet ...
                      return "";
                    }
                    if (theoryExams.length === 0 && hasPassedTheoryExam && student.isSchoolChanger) {
                      return "Ja (Fahrschulwechsler)";
                    }
                    if (!lastTheoryExamTakenAt) {
                      if (!nextTheoryExam) {
                        return "Nein";
                      }
                      return `Nein (1. Versuch am ${formatDate(nextTheoryExam.start)})`;
                    } else {
                      const result = lastTheoryExamResult
                        ? lastTheoryExamResult === "passed"
                          ? "Ja"
                          : "Nein"
                        : "Unbekannt";
                      if (nextTheoryExam) {
                        const attempts = numTheoryExamsTaken === 1 ? "ein Versuch" : `${numTheoryExamsTaken} Versuche`;
                        return `${result} (${attempts} bisher, nächster am ${formatDate(nextTheoryExam.start)})`;
                      } else if (numTheoryExamsTaken === 1) {
                        return `${result} (1. Versuch am ${formatDate(lastTheoryExamTakenAt)})`;
                      } else {
                        return `${result} (${numTheoryExamsTaken} Versuche, letzter am ${formatDate(lastTheoryExamTakenAt)})`;
                      }
                    }
                  })()}
                </Typography>
              </Row>
              {lastTheoryExamResults && Object.keys(lastTheoryExamResults).length > 0 && (
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {Object.entries(lastTheoryExamResults)
                    .map(
                      ([trainingId, result]) =>
                        `${drivingLicenseClassByTrainingId.get(trainingId) ?? trainingId}: ${
                          result === "passed" ? "bestanden" : "nicht bestanden"
                        }`,
                    )
                    .join(", ")}
                </Typography>
              )}
            </Column>
          )}
        </Column>
        {student.isSchoolChanger && (
          <IconButton onClick={() => setIsHasPassedTheoryExamEditable(true)}>
            <EditIcon />
          </IconButton>
        )}
      </Row>
    </>
  );
}

function _HasPassedTheoryExamForm(props: { stopEditMode: () => void }) {
  useOnEscapeKeyDown(props.stopEditMode);
  return (
    <EditBase redirect={false} mutationMode="pessimistic">
      <Form2>
        <_HasPassedTheoryExamInputField {...props} />
      </Form2>
    </EditBase>
  );
}

function _HasPassedTheoryExamInputField(props: { stopEditMode: () => void }) {
  const [update, { isLoading }] = useUpdate();
  const student = useRecordContext<Student>();
  const { id } = student;
  const updateStudent = (data: DeepPartial<Student>) =>
    update(
      "students",
      { id, data, previousData: student },
      {
        onSuccess: props.stopEditMode,
        onError: (error) => handleError("Fehler beim Speichern.", error),
      },
    );

  return (
    <BooleanInput
      variant="checkbox"
      source="theoryKPIs.hasPassedTheoryExam"
      label="hat Theorieprüfung bestanden"
      helperText={false}
      size="small"
      className="whitespace-nowrap"
      onChange={(event) => {
        if (event.target.checked) {
          void updateStudent({ theoryKPIs: { hasPassedTheoryExam: true } });
        } else {
          // UX: if `hasPassedTheoryExam` is toggled from `true` to `false`,
          //     we also set the feature toggles `canLearnTheoryInMobileApp`
          //     and `canBookTheoryLessons` to `true` ...
          void updateStudent({
            theoryKPIs: { hasPassedTheoryExam: false },
            featureToggles: {
              canLearnTheoryInMobileApp: true,
              canBookTheoryLessons: true,
            },
          });
        }
      }}
      disabled={isLoading}
    />
  );
}
